import { Building2, Check, ChevronDown, Search, Unlink, UserRound } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { EmptyState, Spinner } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { PICKER_PAGE_SIZE, useReferenceSearch } from "@/lib/queries";
import type { FormEntity } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";
import { cn } from "@/lib/utils";

/**
 * Picking the record a reference field points at (ACL-260, ADR-089 in `platform`): a firm, or a contact.
 *
 * Built as `endpoint-picker.tsx` is, for the same reasons: **the server searches and pages**
 * (`GET /forms/{entity}/fields/{key}/references`), because a tenant's contacts are tens of thousands and
 * the browser never holds them all; **the value is the record, not just its id**, so the current choice
 * stays on screen while a search shows other rows; and it **expands in place**, because the drawer's body
 * scrolls and a popover inside it would be clipped. The server decides what may be offered (a rep is
 * offered only the contacts they work) and checks the pick again on save.
 */

/** Where the highlight starts once a search's results are in: on the first result when there is a search
 *  and it found something, else the top row. Never on None or on the pinned current choice while a search
 *  has results, so Enter picks what was typed for. */
export function startIndex(rows: ReadonlyArray<{ key: string; choice: unknown; pinned?: true }>, searching: boolean): number {
  if (!searching) return 0;
  const first = rows.findIndex((r) => r.choice !== null && !r.pinned);
  return first === -1 ? 0 : first;
}

/** A picked record: what is stored, and what the reader reads. */
export interface ReferenceChoice {
  id: number | string;
  label: string;
}

export function ReferencePicker({
  entity, fieldKey, target, value, onChange, allowNone, invalid, id,
  "aria-describedby": describedBy,
}: {
  /** The form the field belongs to: the record being edited is a contact (`lead`) or a firm. */
  entity: FormEntity;
  fieldKey: string;
  /** What the field points at. */
  target: FormEntity | undefined;
  value: ReferenceChoice | null;
  onChange: (value: ReferenceChoice | null) => void;
  /** Offer "None" as a row, for a field that may be left empty. */
  allowNone: boolean;
  invalid?: boolean;
  /** `id` and `aria-describedby` are injected by `<Field>`, which clones its child. */
  id?: string;
  "aria-describedby"?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [active, setActive] = React.useState(0);
  const debounced = useDebounced(search.trim(), 250);
  const listId = React.useId();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const wasOpen = React.useRef(false);
  const noun = target === "lead" ? "contact" : "firm";
  const Icon = target === "lead" ? UserRound : Building2;

  const { data, isPending, error, isFetchingNextPage, hasNextPage, fetchNextPage } = useReferenceSearch(
    entity, fieldKey, debounced, open,
  );
  const loaded = React.useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const same = (a: ReferenceChoice | null, b: { id: number | string } | null) => String(a?.id ?? "") === String(b?.id ?? "");
  // The chosen record pinned on top when the results do not hold it, which is the usual case once
  // anyone types: otherwise the list shows nothing chosen while something very much is.
  const pinned = value && !loaded.some((o) => same(value, o)) ? value : null;
  const rows: Array<{ key: string; choice: ReferenceChoice | null; detail?: string | null; pinned?: true }> = [
    ...(allowNone ? [{ key: "none", choice: null }] : []),
    ...(pinned ? [{ key: `pin-${pinned.id}`, choice: pinned, pinned: true as const }] : []),
    ...loaded.map((o) => ({ key: String(o.id), choice: { id: o.id, label: o.label }, detail: o.detail })),
  ];
  const nothing = loaded.length === 0 && pinned === null;

  React.useEffect(() => {
    if (!open) return;
    setActive(Math.max(0, rows.findIndex((r) => same(value, r.choice))));
    // Only when the panel opens: on every keystroke it would fight the arrow keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // A new search starts at the top until its results arrive; then on the first result, never on None, so
  // typing a name and pressing Enter picks it rather than clearing the field. With no search, the current
  // choice: opening the panel could not know where it sits until the first page was in, since the list was
  // empty then and the choice was pinned, and the page moves it. Only once per search: a further page of
  // the same search leaves the highlight where the arrow keys put it.
  const settledFor = React.useRef<string | null>(null);
  React.useEffect(() => setActive(0), [debounced]);
  React.useEffect(() => {
    if (!open || !data || settledFor.current === debounced) return;
    settledFor.current = debounced;
    setActive(debounced === "" ? Math.max(0, rows.findIndex((r) => same(value, r.choice))) : startIndex(rows, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debounced, data]);
  React.useEffect(() => {
    if (!open) settledFor.current = null;
  }, [open]);
  React.useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, loaded.length]);
  // Collapsing unmounts the input, so focus goes back to the trigger rather than the page's top.
  React.useEffect(() => {
    if (wasOpen.current && !open) triggerRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  const choose = (choice: ReferenceChoice | null) => {
    onChange(choice);
    setOpen(false);
    setSearch("");
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (rows.length === 0) return;
      const next = e.key === "ArrowDown" ? active + 1 : active - 1;
      setActive((next + rows.length) % rows.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Not until the list on screen is the answer to what is in the box: while the search is still
      // on its way (or the typing has not reached it), the rows are the previous search's, or only
      // None and the pinned choice, and Enter would clear the link or pick the wrong record.
      if (isPending || error || search.trim() !== debounced) return;
      const row = rows[active];
      if (row) choose(row.choice);
    } else if (e.key === "Escape") {
      // The first Escape closes the picker. The drawer around it is told not to take it
      // (`data-closes-on-escape`, read by `DialogContent`), because it hears Escape before this does.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  if (!open) {
    return (
      <button
        ref={triggerRef}
        type="button"
        id={id}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        onClick={() => {
          setOpen(true);
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
        className={cn(
          "flex h-8 w-full items-center gap-2 rounded-md border bg-surface px-2.5 text-left shadow-xs",
          "transition-colors hover:bg-surface-hover focus-visible:border-accent-8 focus-visible:outline-none",
          "focus-visible:ring-2 focus-visible:ring-accent-8/25",
          invalid ? "border-danger-9" : "border-border",
        )}
      >
        {value ? (
          <>
            <Icon className="size-4 shrink-0 text-text-subtle" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-sm text-text">{value.label}</span>
          </>
        ) : (
          <>
            <Unlink className="size-4 shrink-0 text-text-subtle" aria-hidden />
            <span className="flex-1 text-sm text-text-subtle">Choose a {noun}</span>
          </>
        )}
        <ChevronDown className="size-4 shrink-0 text-text-subtle" aria-hidden />
      </button>
    );
  }

  return (
    <div data-reference-picker data-closes-on-escape className="space-y-2 rounded-md border border-accent-8 bg-surface p-2 ring-2 ring-accent-8/25">
      <Input
        ref={inputRef}
        id={id}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          // Only when focus leaves the picker: clicking a row moves focus within it.
          if (!e.currentTarget.closest("[data-reference-picker]")?.contains(e.relatedTarget)) setOpen(false);
        }}
        placeholder={target === "lead" ? "Search by name, phone or email" : "Search by name or code"}
        leading={<Search />}
        role="combobox"
        aria-expanded
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={!nothing && rows[active] ? `${listId}-${rows[active].key}` : undefined}
        aria-describedby={describedBy}
        aria-label={`Search ${noun}s`}
      />

      {isPending ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : error ? (
        <p className="px-1 py-3 text-xs text-danger-11">Could not search. Close and try again.</p>
      ) : nothing && !allowNone ? (
        <EmptyState
          icon={Icon}
          title={debounced ? "Nothing matches" : `No ${noun}s to pick`}
          description={
            debounced
              ? `No ${noun} matches "${debounced}".`
              : target === "lead"
                ? "Only the contacts you can open are offered."
                : undefined
          }
          className="py-5"
        />
      ) : (
        <ul ref={listRef} id={listId} role="listbox" aria-label={`${noun}s`} className="max-h-56 space-y-1 overflow-y-auto scrollbar-subtle">
          {rows.map((row, i) => {
            const chosen = same(value, row.choice);
            return (
              <li key={row.key}>
                <button
                  type="button"
                  id={`${listId}-${row.key}`}
                  role="option"
                  data-index={i}
                  aria-selected={chosen}
                  tabIndex={-1}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(row.choice)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-md border px-3 py-1.5 text-left transition-colors focus:outline-none",
                    chosen ? "border-accent-8 bg-accent-2" : "border-transparent",
                    i === active && !chosen && "bg-surface-hover",
                  )}
                >
                  {row.choice === null ? (
                    <>
                      <Unlink className="size-4 shrink-0 text-text-subtle" aria-hidden />
                      <span className="flex-1 text-sm">None</span>
                    </>
                  ) : (
                    <>
                      <Icon className="size-4 shrink-0 text-text-subtle" aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-text">{row.choice.label}</span>
                        {row.detail && <span className="block truncate text-xs text-text-muted">{row.detail}</span>}
                      </span>
                    </>
                  )}
                  {row.pinned && <Badge tone="outline" size="sm">Current</Badge>}
                  {chosen && <Check className="size-4 shrink-0 text-accent-11" aria-hidden />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center justify-between gap-2 px-1">
        <p className="text-xs text-text-subtle" aria-live="polite">
          {isPending ? "Searching…" : `${loaded.length} shown${debounced ? " matching" : ""}`}
        </p>
        {hasNextPage && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="text-xs text-accent-11 hover:underline disabled:opacity-50"
          >
            {isFetchingNextPage ? "Loading…" : `Load ${PICKER_PAGE_SIZE} more`}
          </button>
        )}
      </div>
    </div>
  );
}
